import { useState, useCallback } from "react";
import { ArrowUpDown, Upload, Download, FileText, Loader2, GripVertical, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  { question: "Is organizing PDF pages free?", answer: "Yes, completely free with no signup required." },
  { question: "How do I reorder pages?", answer: "Drag and drop page thumbnails to rearrange them in any order." },
  { question: "Can I also delete pages?", answer: "Yes, click the X button on any page thumbnail to remove it." },
  { question: "Will organizing affect quality?", answer: "No, original page quality is preserved." },
  { question: "Are my files secure?", answer: "Yes, files auto-delete after 30 minutes." }
];

const PdfOrganizePage = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);
  const [draggedPage, setDraggedPage] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPages(response.data.pages.map((p, i) => ({ ...p, order: i })));
      }
    } catch (error) {
      toast.error("Failed to load preview");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePageDragStart = (e, index) => {
    setDraggedPage(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handlePageDragOver = (e, index) => {
    e.preventDefault();
    if (draggedPage === null || draggedPage === index) return;

    const newPages = [...pages];
    const [removed] = newPages.splice(draggedPage, 1);
    newPages.splice(index, 0, removed);
    setPages(newPages);
    setDraggedPage(index);
  };

  const handlePageDragEnd = () => {
    setDraggedPage(null);
  };

  const movePage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= pages.length) return;
    const newPages = [...pages];
    const [removed] = newPages.splice(fromIndex, 1);
    newPages.splice(toIndex, 0, removed);
    setPages(newPages);
  };

  const handleOrganize = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    // Check if order changed
    const originalOrder = pages.map((_, i) => i + 1).join(",");
    const newOrder = pages.map((p) => p.page).join(",");
    
    if (originalOrder === newOrder) {
      toast.error("Page order hasn't changed");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("page_order", newOrder);

    try {
      const response = await axios.post(`${API}/pdf-organize`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Organize failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        await downloadFile(`${API}/download/${result.files[0].id}`, result.files[0].name || "organized.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPages([]);
  };

  return (
    <>
      <SEOHead
        title="Organize PDF Pages Free Online | Rearrange PDF"
        description="Organize and rearrange PDF pages by drag and drop. Reorder, delete, and save. Free online PDF organizer. No signup required."
        canonicalPath="/pdf-organize"
        keywords="organize PDF, rearrange PDF pages, reorder PDF, PDF page organizer free, sort PDF pages"
      />
      <div className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
                <ArrowUpDown className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Organize PDF Pages Free Online
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Rearrange pages in your PDF by dragging and dropping. Delete unwanted pages too.
              No signup required.
            </p>
          </div>

          {/* Upload Area */}
          {!result && pages.length === 0 && (
            <div className="space-y-6 animate-fade-in-up animate-delay-100">
              <div
                className={`upload-zone ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleFileDrop}
                onClick={() => document.getElementById("file-input").click()}
                data-testid="upload-zone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              {isLoadingPreview ? (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="font-medium">Loading preview...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Page Preview Grid */}
        {!result && pages.length > 0 && (
          <div className="space-y-6 animate-fade-in-up" data-testid="preview-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{file?.name}</h2>
                <p className="text-sm text-muted-foreground">{pages.length} pages</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground bg-secondary p-3">
              Drag pages to reorder them. Use the arrows to move pages up or down.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {pages.map((page, index) => (
                <div
                  key={`${page.page}-${index}`}
                  draggable
                  onDragStart={(e) => handlePageDragStart(e, index)}
                  onDragOver={(e) => handlePageDragOver(e, index)}
                  onDragEnd={handlePageDragEnd}
                  className={`border-2 transition-all p-3 cursor-grab active:cursor-grabbing ${
                    draggedPage === index
                      ? "border-[#FF3B30] opacity-50"
                      : "border-border hover:border-black"
                  }`}
                  data-testid={`page-${index}`}
                >
                  <div className="flex items-center justify-center mb-2">
                    <GripVertical className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="aspect-[3/4] bg-secondary flex items-center justify-center mb-3 overflow-hidden">
                    <img
                      src={`${API}/download/${page.thumbnail}`}
                      alt={`Page ${page.page}`}
                      className="max-w-full max-h-full object-contain pointer-events-none"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-mono">
                      Page {page.page}
                      {index + 1 !== page.page && (
                        <span className="text-[#FF3B30] ml-1">→ {index + 1}</span>
                      )}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => movePage(index, index - 1)}
                        disabled={index === 0}
                        className="p-1 hover:bg-secondary rounded disabled:opacity-30"
                        data-testid={`move-up-${index}`}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => movePage(index, index + 1)}
                        disabled={index === pages.length - 1}
                        className="p-1 hover:bg-secondary rounded disabled:opacity-30"
                        data-testid={`move-down-${index}`}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex-1 h-12 rounded-sm"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleOrganize}
                disabled={isProcessing}
                className="flex-1 h-12 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift"
                data-testid="apply-button"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <ArrowUpDown className="w-5 h-5 mr-2" />
                    Save New Order
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDF Reorganized Successfully
              </h2>
              <p className="text-muted-foreground mb-6">
                {result.message}
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Organize Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        <ToolContentSection toolKey="pdf-organize" content={toolContent["pdf-organize"]} />
        <FAQSection faqs={faqs} />
        <RelatedTools currentTool="pdf-organize" />
      </div>
    </div>
    </>
  );
};

export default PdfOrganizePage;
